import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft, MailCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import Logo from "@/components/layout/Logo";

export default function ForgotPassword() {
  const [identifier, setIdentifier] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { identifier: identifier.trim() });
      setSent(data.devToken || true);
      toast.success("If the account exists, a reset link has been generated.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-appbg px-6">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md card-surface p-8">
        <Logo />
        <h2 className="mt-6 font-heading text-2xl font-bold text-slate-900">Reset your password</h2>
        <p className="mt-1 text-sm text-slate-500">Enter your username or email and we'll generate a reset link.</p>

        {sent ? (
          <div className="mt-6 rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-800">
            <MailCheck className="mb-2 h-5 w-5" />
            A reset link has been generated. {typeof sent === "string" && (
              <>For this demo, <Link to={`/reset-password?token=${sent}`} className="font-semibold underline" data-testid="dev-reset-link">click here to reset</Link>.</>
            )}
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <input
              data-testid="forgot-identifier-input"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="Username or email"
              className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm outline-none focus:border-brand-400 focus:bg-white focus:ring-2 focus:ring-brand-200"
              required
            />
            <Button type="submit" disabled={loading} data-testid="forgot-submit-button" className="h-11 w-full rounded-xl bg-brand-700 text-white hover:bg-brand-800">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
            </Button>
          </form>
        )}

        <Link to="/login" className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:underline">
          <ArrowLeft className="h-4 w-4" /> Back to login
        </Link>
      </motion.div>
    </div>
  );
}
